import type { ReactNode } from "react";
import { Card, Loader, Stack, Title } from "@mantine/core";
import type { UseQueryResult } from "@tanstack/react-query";
import { ErrorAlert } from "./ErrorAlert";

interface QueryCardProps<T> {
  // String → rendered as <Title order={4}>; node → rendered as-is (for headers
  // with extra controls like pagination buttons).
  readonly title: ReactNode;
  readonly query: UseQueryResult<T>;
  // Additional queries whose pending/error state should also surface in this
  // card. The first card on a route typically watches the chain head here.
  readonly alsoWatch?: readonly UseQueryResult<unknown>[];
  readonly children: (data: T) => ReactNode;
}

export function QueryCard<T>({
  title,
  query,
  alsoWatch = [],
  children,
}: QueryCardProps<T>) {
  const isPending = query.isPending || alsoWatch.some((q) => q.isPending);
  return (
    <Card withBorder padding="md">
      <Stack gap="xs">
        {typeof title === "string" ? <Title order={4}>{title}</Title> : title}
        {isPending ? <Loader size="sm" /> : null}
        {alsoWatch.map((q, i) =>
          q.isError ? <ErrorAlert key={i} error={q.error} /> : null,
        )}
        {query.isError ? <ErrorAlert error={query.error} /> : null}
        {query.data !== undefined ? children(query.data) : null}
      </Stack>
    </Card>
  );
}
